<template>
  <div class="navbar">
    <!-- 左侧 -->
    <div class="navbar-left">
      <!-- 侧边栏折叠按钮（仅手机端） -->
      <el-button v-if="isMobile" type="text" :icon="sidebar.opened ? 'el-icon-s-fold' : 'el-icon-s-unfold'" @click="toggleSidebar" class="sidebar-toggle" />

      <!-- 刷新按钮 -->
      <el-tooltip content="刷新页面" placement="bottom">
        <el-button
          type="text"
          icon="el-icon-refresh"
          @click="refreshView"
          class="sidebar-toggle"
        />
      </el-tooltip>

      <!-- 面包屑导航 -->
      <el-breadcrumb separator="/" class="breadcrumb">
        <el-breadcrumb-item
          v-for="item in breadcrumbList"
          :key="item.path"
          :to="{ path: item.path }"
        >
          {{ item.meta.title }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <!-- 右侧 -->
    <div class="navbar-right">
      <!-- 全屏按钮 -->
      <el-tooltip content="全屏" placement="bottom">
        <el-button
          type="text"
          :icon="isFullscreen ? 'el-icon-copy-document' : 'el-icon-full-screen'"
          @click="toggleFullscreen"
          class="navbar-btn"
        />
      </el-tooltip>

      <!-- 用户头像下拉菜单 -->
      <el-dropdown @command="handleCommand" class="user-dropdown">
        <div class="user-info">
          <img :src="userInfo.avatar || defaultAvatar" alt="头像" class="user-avatar" />
          <div class="user-details">
            <span class="user-name">{{ userInfo.nickname || userInfo.username || "管理员" }}</span>
            <span class="user-role" v-if="userRole">{{ userRole }}</span>
          </div>
          <i class="el-icon-arrow-down"></i>
        </div>

        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="profile">
            <i class="el-icon-user"></i>
            个人中心
          </el-dropdown-item>
          <el-dropdown-item v-if="userInfo.role === 'super_admin'" command="settings">
            <i class="el-icon-setting"></i>
            系统设置
          </el-dropdown-item>
          <el-dropdown-item divided command="logout">
            <i class="el-icon-switch-button"></i>
            退出登录
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import defaultAvatar from "@/assets/default-avatar.png";

export default {
  name: "NavbarComponent",
  data() {
    return {
      defaultAvatar,
      isFullscreen: false,
    };
  },
  computed: {
    ...mapGetters("user", ["userInfo", "roles"]),
    ...mapGetters("app", ["sidebar", "device"]),
    isMobile() {
      return this.device === "mobile";
    },
    breadcrumbList() {
      const matched = this.$route.matched.filter(
        item => item.meta && item.meta.title && !item.meta.hidden
      );

      // 添加首页到面包屑开头
      const homePath = this.$store.state.tabs.tabs[0]?.path || '/dashboard';
      const homeTitle = homePath === '/my-tasks' ? '我的任务' : '首页';
      const breadcrumb = [
        {
          path: homePath,
          meta: { title: homeTitle },
        },
      ];

      // 如果不是首页，添加其他路径
      if (this.$route.path !== homePath) {
        breadcrumb.push(...matched);
      }

      return breadcrumb;
    },
    // 用户角色显示
    userRole() {
      const roleMap = {
        super_admin: "超级管理员",
        operator: "运营管理员",
        staff: "普通员工",
      };
      return roleMap[this.userInfo.role] || null;
    },
  },
  methods: {
    // 刷新当前页面
    refreshView() {
      const { path, query } = this.$route;
      this.$router.replace({ path: "/redirect" + path, query }, () => {}, () => {});
    },

    // 切换侧边栏
    toggleSidebar() {
      this.$store.dispatch("app/toggleSideBar");
    },

    // 切换全屏
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        this.isFullscreen = true;
      } else {
        document.exitFullscreen();
        this.isFullscreen = false;
      }
    },

    // 处理下拉菜单命令
    handleCommand(command) {
      switch (command) {
        case "profile":
          this.$router.push("/user/profile");
          break;
        case "settings":
          this.$router.push("/system/menu");
          break;
        case "logout":
          this.logout();
          break;
      }
    },

    // 退出登录
    logout() {
      this.$confirm("确定要退出登录吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          this.$store.dispatch("user/logout").then(() => {
            this.$router.push("/login").catch(() => { /* ignore */ });
            this.$message.success("退出成功");
          });
        })
        .catch(() => {
          // 取消退出
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.navbar {
  height: $header-height;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 $spacing-md;
  background-color: $bg-white;
  box-shadow: $box-shadow-light;

  .navbar-left {
    display: flex;
    align-items: center;

    .sidebar-toggle {
      margin-right: $spacing-md;
      font-size: 18px;
      color: $text-primary;

      &:hover {
        color: $primary-color;
      }
    }

    .breadcrumb {
      font-size: $font-size-sm;
    }
  }

  .navbar-right {
    display: flex;
    align-items: center;

    .navbar-btn {
      margin-right: $spacing-md;
      font-size: 16px;
      color: $text-primary;

      &:hover {
        color: $primary-color;
      }
    }

    .user-dropdown {
      .user-info {
        display: flex;
        align-items: center;
        cursor: pointer;
        padding: 0 $spacing-sm;

        .user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          margin-right: $spacing-sm;
          object-fit: cover;
        }

        .user-details {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          margin-right: $spacing-xs;

          .user-name {
            color: $text-primary;
            font-size: $font-size-base;
            font-weight: 500;
            line-height: 1.2;
          }

          .user-role {
            color: $text-secondary;
            font-size: $font-size-sm;
            line-height: 1.2;
            margin-top: 2px;
          }
        }

        &:hover {
          background-color: $bg-color;
          border-radius: $border-radius-base;
        }
      }
    }
  }
}

// 响应式
@media (max-width: 768px) {
  .navbar {
    padding: 0 $spacing-sm;

    .navbar-left {
      .breadcrumb {
        display: none;
      }
    }

    .navbar-right {
      .navbar-btn {
        margin-right: $spacing-sm;
      }

      .user-dropdown {
        .user-info {
          .user-details {
            .user-name {
              font-size: $font-size-sm;
            }

            .user-role {
              font-size: $font-size-xs;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .navbar {
    .navbar-right {
      .user-dropdown {
        .user-info {
          .user-details {
            display: none;
          }
        }
      }
    }
  }
}
</style>
